import React, { useState, useEffect } from 'react';
import api from '../../utils/api';
import { FiPlus, FiEdit, FiTrash2, FiFileText, FiRefreshCw, FiTruck, FiUser, FiDollarSign, FiCalendar } from 'react-icons/fi';
import toast from 'react-hot-toast';
import { formatarData } from '../../utils/dateUtils';

const ContratosOnibus = () => {
  const [contratos, setContratos] = useState([]);
  const [viagens, setViagens] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [contratoEdit, setContratoEdit] = useState(null);
  const [formData, setFormData] = useState({
    viagem_id: '',
    empresa_nome: '',
    empresa_cnpj: '',
    empresa_endereco: '',
    empresa_telefone: '',
    empresa_email: '',
    onibus_placa: '',
    onibus_modelo: '',
    onibus_ano: '',
    onibus_capacidade: '',
    motorista_nome: '',
    motorista_cpf: '',
    motorista_cnh: '',
    motorista_telefone: '',
    valor_contrato: '',
    data_contrato: '',
    observacoes: '',
    status: 'pendente'
  });

  useEffect(() => {
    fetchContratos();
    fetchViagens();
  }, []);

  const fetchContratos = async () => {
    try {
      const response = await api.get('/api/contratos-onibus');
      setContratos(response.data);
    } catch (error) {
      toast.error('Erro ao carregar contratos');
    } finally {
      setLoading(false);
    }
  };

  const fetchViagens = async () => {
    try {
      const response = await api.get('/api/viagens');
      setViagens(response.data);
    } catch (error) {
      console.error('Erro ao carregar viagens:', error);
    }
  };

  const handleOpenModal = (contrato = null) => {
    if (contrato) {
      setContratoEdit(contrato);
      setFormData({
        viagem_id: contrato.viagem_id || '',
        empresa_nome: contrato.empresa_nome || '',
        empresa_cnpj: contrato.empresa_cnpj || '',
        empresa_endereco: contrato.empresa_endereco || '',
        empresa_telefone: contrato.empresa_telefone || '',
        empresa_email: contrato.empresa_email || '',
        onibus_placa: contrato.onibus_placa || '',
        onibus_modelo: contrato.onibus_modelo || '',
        onibus_ano: contrato.onibus_ano || '',
        onibus_capacidade: contrato.onibus_capacidade || '',
        motorista_nome: contrato.motorista_nome || '',
        motorista_cpf: contrato.motorista_cpf || '',
        motorista_cnh: contrato.motorista_cnh || '',
        motorista_telefone: contrato.motorista_telefone || '',
        valor_contrato: contrato.valor_contrato || '',
        data_contrato: contrato.data_contrato || '',
        observacoes: contrato.observacoes || '',
        status: contrato.status || 'pendente'
      });
    } else {
      setContratoEdit(null);
      setFormData({
        viagem_id: '',
        empresa_nome: '',
        empresa_cnpj: '',
        empresa_endereco: '',
        empresa_telefone: '',
        empresa_email: '',
        onibus_placa: '',
        onibus_modelo: '',
        onibus_ano: '',
        onibus_capacidade: '',
        motorista_nome: '',
        motorista_cpf: '',
        motorista_cnh: '',
        motorista_telefone: '',
        valor_contrato: '',
        data_contrato: new Date().toISOString().split('T')[0],
        observacoes: '',
        status: 'pendente'
      });
    }
    setModalOpen(true);
  };

  const handleCloseModal = () => {
    setModalOpen(false);
    setContratoEdit(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      if (contratoEdit) {
        await api.put(`/api/contratos-onibus/${contratoEdit.id}`, formData);
        toast.success('Contrato atualizado com sucesso!');
      } else {
        await api.post('/api/contratos-onibus', formData);
        toast.success('Contrato criado com sucesso!');
      }
      fetchContratos();
      handleCloseModal();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Erro ao salvar contrato');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Tem certeza que deseja excluir este contrato?')) {
      return;
    }

    try {
      await api.delete(`/api/contratos-onibus/${id}`);
      toast.success('Contrato excluído com sucesso!');
      fetchContratos();
    } catch (error) {
      toast.error('Erro ao excluir contrato');
    }
  };

  const handleGerarPDF = async (id) => {
    try {
      const response = await api.get(`/api/contratos-onibus/${id}/pdf`, {
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `contrato-onibus-${id}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success('PDF gerado com sucesso!');
    } catch (error) {
      toast.error('Erro ao gerar PDF');
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'pendente':
        return 'bg-yellow-100 text-yellow-800';
      case 'confirmado':
        return 'bg-green-100 text-green-800';
      case 'cancelado':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusLabel = (status) => {
    switch (status) {
      case 'pendente':
        return 'Pendente';
      case 'confirmado':
        return 'Confirmado';
      case 'cancelado':
        return 'Cancelado';
      default:
        return status;
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-sky-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-white">Contratos de Ônibus</h1>
          <p className="text-white/70 mt-1">Gerencie os contratos de empresas de transporte</p>
        </div>
        <div className="flex gap-2">
          <button
            onClick={fetchContratos}
            className="flex items-center gap-2 px-4 py-2 bg-sky-600 text-white rounded-lg hover:bg-sky-700 transition-colors"
          >
            <FiRefreshCw size={18} />
            Atualizar
          </button>
          <button
            onClick={() => handleOpenModal()}
            className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
          >
            <FiPlus size={18} />
            Novo Contrato
          </button>
        </div>
      </div>

      {/* Lista de Contratos */}
      <div className="bg-sky-50 rounded-lg shadow overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Viagem
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Empresa
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Ônibus
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Motorista
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Valor
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Ações
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {contratos.length === 0 ? (
                <tr>
                  <td colSpan="7" className="px-6 py-4 text-center text-gray-500">
                    Nenhum contrato encontrado
                  </td>
                </tr>
              ) : (
                contratos.map((contrato) => (
                  <tr key={contrato.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4">
                      <div className="text-sm font-medium text-gray-900">{contrato.viagem_titulo || 'N/A'}</div>
                      <div className="text-sm text-gray-500">
                        {contrato.destino} - {formatarData(contrato.data_ida)}
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="text-sm font-medium text-gray-900">{contrato.empresa_nome}</div>
                      <div className="text-sm text-gray-500">CNPJ: {contrato.empresa_cnpj}</div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="text-sm font-medium text-gray-900">Placa: {contrato.onibus_placa}</div>
                      {contrato.onibus_modelo && (
                        <div className="text-sm text-gray-500">{contrato.onibus_modelo}</div>
                      )}
                    </td>
                    <td className="px-6 py-4">
                      <div className="text-sm font-medium text-gray-900">{contrato.motorista_nome}</div>
                      <div className="text-sm text-gray-500">CNH: {contrato.motorista_cnh}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-gray-900">
                        R$ {parseFloat(contrato.valor_contrato).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(contrato.status)}`}>
                        {getStatusLabel(contrato.status)}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => handleGerarPDF(contrato.id)}
                          className="text-blue-600 hover:text-blue-900"
                          title="Gerar PDF"
                        >
                          <FiFileText size={18} />
                        </button>
                        <button
                          onClick={() => handleOpenModal(contrato)}
                          className="text-primary-600 hover:text-primary-900"
                          title="Editar contrato"
                        >
                          <FiEdit size={18} />
                        </button>
                        <button
                          onClick={() => handleDelete(contrato.id)}
                          className="text-red-600 hover:text-red-900"
                          title="Excluir contrato"
                        >
                          <FiTrash2 size={18} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Modal de Cadastro/Edição */}
      {modalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-2xl font-bold text-gray-900">
                  {contratoEdit ? 'Editar Contrato' : 'Novo Contrato'}
                </h2>
                <button
                  onClick={handleCloseModal}
                  className="text-gray-400 hover:text-gray-600"
                >
                  ✕
                </button>
              </div>

              <form onSubmit={handleSubmit} className="space-y-4">
                {/* Informações da Viagem */}
                <div className="bg-gray-50 p-4 rounded-lg">
                  <h3 className="font-semibold text-gray-900 mb-3">Viagem</h3>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Viagem *
                    </label>
                    <select
                      value={formData.viagem_id}
                      onChange={(e) => setFormData({ ...formData, viagem_id: e.target.value })}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-sky-500 bg-white text-gray-800"
                      required
                    >
                      <option value="">Selecione uma viagem...</option>
                      {viagens.map((viagem) => (
                        <option key={viagem.id} value={viagem.id}>
                          {viagem.titulo} - {formatarData(viagem.data_ida)}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                {/* Informações da Empresa */}
                <div className="bg-gray-50 p-4 rounded-lg">
                  <h3 className="font-semibold text-gray-900 mb-3">Dados da Empresa</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Nome da Empresa *
                      </label>
                      <input
                        type="text"
                        value={formData.empresa_nome}
                        onChange={(e) => setFormData({ ...formData, empresa_nome: e.target.value })}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-sky-500 bg-white text-gray-800"
                        required
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        CNPJ *
                      </label>
                      <input
                        type="text"
                        value={formData.empresa_cnpj}
                        onChange={(e) => {
                          const value = e.target.value.replace(/\D/g, '');
                          const formatted = value.replace(/(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})/, '$1.$2.$3/$4-$5');
                          setFormData({ ...formData, empresa_cnpj: formatted });
                        }}
                        maxLength="18"
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-sky-500 bg-white text-gray-800"
                        placeholder="00.000.000/0000-00"
                        required
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Endereço
                      </label>
                      <input
                        type="text"
                        value={formData.empresa_endereco}
                        onChange={(e) => setFormData({ ...formData, empresa_endereco: e.target.value })}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-sky-500 bg-white text-gray-800"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Telefone
                      </label>
                      <input
                        type="text"
                        value={formData.empresa_telefone}
                        onChange={(e) => {
                          const value = e.target.value.replace(/\D/g, '');
                          const formatted = value.replace(/(\d{2})(\d{5})(\d{4})/, '($1) $2-$3');
                          setFormData({ ...formData, empresa_telefone: formatted });
                        }}
                        maxLength="15"
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-sky-500 bg-white text-gray-800"
                        placeholder="(00) 00000-0000"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        E-mail
                      </label>
                      <input
                        type="email"
                        value={formData.empresa_email}
                        onChange={(e) => setFormData({ ...formData, empresa_email: e.target.value })}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-sky-500 bg-white text-gray-800"
                      />
                    </div>
                  </div>
                </div>

                {/* Informações do Ônibus */}
                <div className="bg-gray-50 p-4 rounded-lg">
                  <h3 className="font-semibold text-gray-900 mb-3">Dados do Ônibus</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Placa *
                      </label>
                      <input
                        type="text"
                        value={formData.onibus_placa}
                        onChange={(e) => setFormData({ ...formData, onibus_placa: e.target.value.toUpperCase() })}
                        maxLength="7"
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-sky-500 bg-white text-gray-800"
                        placeholder="ABC1234"
                        required
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Modelo
                      </label>
                      <input
                        type="text"
                        value={formData.onibus_modelo}
                        onChange={(e) => setFormData({ ...formData, onibus_modelo: e.target.value })}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-sky-500 bg-white text-gray-800"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Ano
                      </label>
                      <input
                        type="number"
                        value={formData.onibus_ano}
                        onChange={(e) => setFormData({ ...formData, onibus_ano: e.target.value })}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-sky-500 bg-white text-gray-800"
                        min="1900"
                        max={new Date().getFullYear() + 1}
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Capacidade
                      </label>
                      <input
                        type="number"
                        value={formData.onibus_capacidade}
                        onChange={(e) => setFormData({ ...formData, onibus_capacidade: e.target.value })}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-sky-500 bg-white text-gray-800"
                        min="1"
                      />
                    </div>
                  </div>
                </div>

                {/* Informações do Motorista */}
                <div className="bg-gray-50 p-4 rounded-lg">
                  <h3 className="font-semibold text-gray-900 mb-3">Dados do Motorista</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Nome do Motorista *
                      </label>
                      <input
                        type="text"
                        value={formData.motorista_nome}
                        onChange={(e) => setFormData({ ...formData, motorista_nome: e.target.value })}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-sky-500 bg-white text-gray-800"
                        required
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        CPF *
                      </label>
                      <input
                        type="text"
                        value={formData.motorista_cpf}
                        onChange={(e) => {
                          const value = e.target.value.replace(/\D/g, '');
                          const formatted = value.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, '$1.$2.$3-$4');
                          setFormData({ ...formData, motorista_cpf: formatted });
                        }}
                        maxLength="14"
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-sky-500 bg-white text-gray-800"
                        placeholder="000.000.000-00"
                        required
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        CNH *
                      </label>
                      <input
                        type="text"
                        value={formData.motorista_cnh}
                        onChange={(e) => setFormData({ ...formData, motorista_cnh: e.target.value })}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-sky-500 bg-white text-gray-800"
                        required
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Telefone
                      </label>
                      <input
                        type="text"
                        value={formData.motorista_telefone}
                        onChange={(e) => {
                          const value = e.target.value.replace(/\D/g, '');
                          const formatted = value.replace(/(\d{2})(\d{5})(\d{4})/, '($1) $2-$3');
                          setFormData({ ...formData, motorista_telefone: formatted });
                        }}
                        maxLength="15"
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-sky-500 bg-white text-gray-800"
                        placeholder="(00) 00000-0000"
                      />
                    </div>
                  </div>
                </div>

                {/* Informações do Contrato */}
                <div className="bg-gray-50 p-4 rounded-lg">
                  <h3 className="font-semibold text-gray-900 mb-3">Informações do Contrato</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Valor do Contrato (R$) *
                      </label>
                      <input
                        type="number"
                        step="0.01"
                        value={formData.valor_contrato}
                        onChange={(e) => setFormData({ ...formData, valor_contrato: e.target.value })}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-sky-500 bg-white text-gray-800"
                        required
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Data do Contrato *
                      </label>
                      <input
                        type="date"
                        value={formData.data_contrato}
                        onChange={(e) => setFormData({ ...formData, data_contrato: e.target.value })}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-sky-500 bg-white text-gray-800"
                        required
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Status
                      </label>
                      <select
                        value={formData.status}
                        onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-sky-500 bg-white text-gray-800"
                      >
                        <option value="pendente">Pendente</option>
                        <option value="confirmado">Confirmado</option>
                        <option value="cancelado">Cancelado</option>
                      </select>
                    </div>
                  </div>
                  <div className="mt-4">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Observações
                    </label>
                    <textarea
                      value={formData.observacoes}
                      onChange={(e) => setFormData({ ...formData, observacoes: e.target.value })}
                      rows="3"
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-sky-500 bg-white text-gray-800"
                      placeholder="Informações adicionais sobre o contrato..."
                    />
                  </div>
                </div>

                <div className="flex gap-4 justify-end">
                  <button
                    type="button"
                    onClick={handleCloseModal}
                    className="px-6 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
                  >
                    Cancelar
                  </button>
                  <button
                    type="submit"
                    className="px-6 py-2 bg-sky-600 text-white rounded-lg hover:bg-sky-700"
                  >
                    {contratoEdit ? 'Atualizar' : 'Criar'} Contrato
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ContratosOnibus;
